// Countdown timer component for supplier tasks

import React, { useState, useEffect } from 'react'
import styles from './CountdownTimer.module.css'

interface CountdownTimerProps {
  targetTime: string | Date
  taskName?: string
  variant?: 'default' | 'compact' | 'inline'
  showLabel?: boolean
  onComplete?: () => void
}

interface TimeRemaining {
  days: number
  hours: number
  minutes: number
  seconds: number
  total: number
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetTime,
  taskName,
  variant = 'default',
  showLabel = true,
  onComplete
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0
  })
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    const target = new Date(targetTime).getTime()
    
    const updateTimer = () => {
      const now = new Date().getTime()
      const difference = target - now

      if (difference <= 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          total: 0
        })
        setIsActive(false)
        if (onComplete) {
          onComplete()
        }
        return
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)

      setTimeRemaining({
        days,
        hours,
        minutes,
        seconds,
        total: difference
      })
    }

    updateTimer() // Initial call
    const timer = setInterval(updateTimer, 1000)

    return () => clearInterval(timer)
  }, [targetTime, onComplete])

  const formatTime = (time: number): string => {
    return time.toString().padStart(2, '0')
  }

  const getUrgencyClass = (): string => {
    const totalMinutes = timeRemaining.total / (1000 * 60)
    if (totalMinutes <= 0) return styles.expired
    if (totalMinutes <= 30) return styles.critical
    if (totalMinutes <= 60) return styles.urgent
    if (totalMinutes <= 120) return styles.warning
    return styles.normal
  }

  const getDisplayText = (): string => {
    if (timeRemaining.total <= 0) {
      return 'Task has started'
    }

    if (timeRemaining.days > 0) {
      return `${timeRemaining.days}d ${timeRemaining.hours}h ${timeRemaining.minutes}m`
    } else if (timeRemaining.hours > 0) {
      return `${timeRemaining.hours}h ${timeRemaining.minutes}m`
    } else if (timeRemaining.minutes > 0) {
      return `${timeRemaining.minutes}m ${formatTime(timeRemaining.seconds)}s`
    } else {
      return `${timeRemaining.seconds}s`
    }
  }

  const getLabel = (): string => {
    if (timeRemaining.total <= 0) {
      return ''
    }

    const totalMinutes = timeRemaining.total / (1000 * 60)
    if (totalMinutes <= 30) {
      return 'Starting very soon!'
    } else if (totalMinutes <= 60) {
      return 'Starting soon'
    } else if (totalMinutes <= 120) {
      return 'Upcoming'
    }
    
    return taskName ? `Until ${taskName}` : 'Time remaining'
  }

  const containerClass = `
    ${styles.container}
    ${styles[variant]}
    ${getUrgencyClass()}
    ${!isActive ? styles.inactive : ''}
  `.trim()

  if (variant === 'inline') {
    return (
      <span className={containerClass}>
        <span className={styles.time}>{getDisplayText()}</span>
        {showLabel && (
          <span className={styles.inlineLabel}> remaining</span>
        )}
      </span>
    )
  }

  return (
    <div className={containerClass}>
      {showLabel && (
        <div className={styles.label}>
          {getLabel()}
        </div>
      )}
      
      <div className={styles.timeDisplay}>
        {variant === 'compact' ? (
          <div className={styles.compactTime}>
            {getDisplayText()}
          </div>
        ) : (
          <div className={styles.timeUnits}>
            {timeRemaining.days > 0 && (
              <div className={styles.timeUnit}>
                <div className={styles.timeValue}>{timeRemaining.days}</div>
                <div className={styles.timeLabel}>day{timeRemaining.days !== 1 ? 's' : ''}</div>
              </div>
            )}
            
            {(timeRemaining.days > 0 || timeRemaining.hours > 0) && (
              <div className={styles.timeUnit}>
                <div className={styles.timeValue}>{formatTime(timeRemaining.hours)}</div>
                <div className={styles.timeLabel}>hour{timeRemaining.hours !== 1 ? 's' : ''}</div>
              </div>
            )}
            
            <div className={styles.timeUnit}>
              <div className={styles.timeValue}>{formatTime(timeRemaining.minutes)}</div>
              <div className={styles.timeLabel}>min{timeRemaining.minutes !== 1 ? 's' : ''}</div>
            </div>
            
            {timeRemaining.days === 0 && timeRemaining.hours === 0 && (
              <div className={styles.timeUnit}>
                <div className={styles.timeValue}>{formatTime(timeRemaining.seconds)}</div>
                <div className={styles.timeLabel}>sec{timeRemaining.seconds !== 1 ? 's' : ''}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {timeRemaining.total <= 0 && (
        <div className={styles.expiredMessage}>
          🚀 Task has started!
        </div>
      )}
    </div>
  )
}